import {handleActions, type Action} from 'redux-actions';
import i18next from 'i18next';
import moment from 'moment-timezone';
import {type Record} from 'immutable';

import Plan from 'web-app/react/entities/plans/model';
import * as PlanHelpers from 'web-app/react/entities/plans/helpers';
import * as EditWeekdayActions from 'web-app/react/plans/manage-plan/edit-weekdays/actions';
import {parseDecimalToFloat} from 'web-app/util/parsers';
import generateUUID from 'web-app/util/uuid';
import * as PublicFundingModalActions from 'web-app/react/modals-container/choose-public-funding-modal/actions';
import * as CostHelpers from 'web-app/react/entities/plans/cost-helpers';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {Billing} from 'web-app/react/entities/plans/model';
import type {Discount} from 'web-app/react/entities/plans/model';

import * as Actions from './actions';
import * as PlanPricingActions from './plan-pricing/actions';
import type {BasePlanActionPayload, UpdateToLaterPriceChangeActionResult} from './types/action-types';
import type {ManagePlanState} from './types/state-types';

const updatePlanPart = (plan: Record<ManagePlanState> & Readonly<ManagePlanState>, partId: string, updater: any) => {
    const index = plan.planParts.findIndex(part => part.planPartId === partId);
    if (index < 0) {
        return plan;
    }

    return plan.merge({
        planParts: plan.planParts.update(index, updater),
    });
};
const findDiscountIndex = (discountId: string, discounts: Discount[]) =>
    discounts.findIndex(discount => discount.discountId === discountId);

const updateDiscountOnPlanPart = (
    plan: Record<ManagePlanState> & Readonly<ManagePlanState>,
    partId: string,
    discountId: string,
    updater: any,
) => {
    return updatePlanPart(plan, partId, part => {
        const discountIndex = findDiscountIndex(discountId, part.discounts);
        if (discountIndex < 0) {
            return part;
        }

        return part.merge({
            discounts: part.discounts.update(discountIndex, updater),
        });
    });
};

const reducer = handleActions(
    {
        [Actions.showCreatePlan.type]: (state, {payload}: Action<BasePlanActionPayload>) => {
            if (!payload) {
                return state;
            }

            const plan = payload.editPlan
                ? payload.editPlan.merge({
                      publicFundingSettings: payload.editPlan?.publicFundingSettings?.merge({
                          // Max funded minutes per day should be null when creating a new plan, so that the default value can be set instead
                          maxFundedMinutes: null,
                      }),
                  })
                : Plan.default;

            return plan.merge({
                id: null,
                pricingGroupId: payload.pricingGroupId,
            });
        },
        [Actions.insertPlan.type]: (state, {payload}: Action<BasePlanActionPayload>) => {
            if (!payload) {
                return state;
            }

            return payload.editPlan.merge({
                id: null,
            });
        },

        [Actions.planPeriodDidChange.type]: (state, {payload}: Action<any>) => {
            if (!payload) {
                return state;
            }
            return state.merge({
                from: moment(payload.validOn),
                to: hasValue(payload.validTo) ? moment(payload.validTo) : state.to,
            });
        },

        [Actions.updateToLaterPriceChange.type]: (state, {payload}: Action<UpdateToLaterPriceChangeActionResult>) => {
            if (!payload) {
                return state;
            }

            return state.merge({
                from: moment(payload.validFrom),
                id: null,
            });
        },

        [Actions.fetchManagePlanDataSuccess.type]: (state, {payload}: Action<any>) => {
            if (!payload) {
                return state;
            }

            return state.merge({
                planParts: state.planParts.map(part => {
                    const newPlanPart = PlanHelpers.alignPlanPartWithSessionFlexibility(part, payload.sessions);

                    // If not billing profile has been set,
                    // there is only 1 plan part,
                    // and there is only one billing profile
                    // set that billing profile on the plan part
                    if (
                        newPlanPart.billingProfileId ||
                        state.planParts.size !== 1 ||
                        payload.billingProfiles.size !== 1
                    ) {
                        return newPlanPart;
                    }

                    const onlyBillingProfile = payload.billingProfiles.first();

                    // If editing plan don't override billing settings, just set the billingProfileId
                    if (state.id) {
                        return newPlanPart.merge({
                            billingProfileId: onlyBillingProfile.billingProfileId,
                        });
                    }

                    return PlanHelpers.setBillingProfileOnPlanPart(newPlanPart, onlyBillingProfile);
                }),
            });
        },

        [Actions.showEditPlan.type]: (state, {payload}) => payload?.editPlan,
        [Actions.simpleViewPlan.type]: (state, {payload}) => payload?.editPlan,
        [Actions.pricingGroupDidChange.type]: (state, {payload}) => {
            if (!payload) {
                return state;
            }

            return state.merge({
                pricingGroupId: payload.pricingGroupType === 'PricingGroup' ? payload.pricingGroupId : null,
                ruleGroupId: payload.pricingGroupType === 'RuleGroup' ? payload.pricingGroupId : null,
            });
        },
        [Actions.updateNote.type]: (state, {payload}) => {
            if (!payload) {
                return state;
            }

            return state.merge({
                note: payload.value,
            });
        },

        [EditWeekdayActions.savePlanWeekday.type]: (state, {payload}) => {
            if (!payload) {
                return state;
            }
            return updatePlanPart(state, payload.planPart.planPartId, () => payload.planPart);
        },
        [Actions.toggleFundable.type]: (state, {payload}: Action<any>) => {
            const {planPartId, bookingId, fundable} = payload;

            return updatePlanPart(state, planPartId, part =>
                part.merge({
                    sessionBookings: part.sessionBookings.map(booking => {
                        if (booking.bookingId === bookingId) {
                            return booking.merge({
                                fundable,
                            });
                        }

                        return booking;
                    }),
                }),
            );
        },
        [Actions.addPlanPart.type]: state => PlanHelpers.addPlanPartToPlan(state),
        [Actions.removePlanPart.type]: (state, {payload}) =>
            state.merge({
                planParts: state.planParts.filter(part => part.planPartId !== payload.planPartId),
            }),
        [Actions.removeBooking.type]: (state, {payload}) => {
            const {bookingId, planPartId, session} = payload;
            return updatePlanPart(state, planPartId, part => {
                return PlanHelpers.removeSessionBookingFromPlanPart(part, bookingId, session);
            });
        },
        [Actions.removeProductBooking.type]: (state, {payload}) => {
            const {planPartId, productBookingId, day} = payload;

            return updatePlanPart(state, planPartId, planPart =>
                PlanHelpers.removeProductBookingFromPlanPart(planPart, productBookingId, day),
            );
        },
        [Actions.updateChargeRuleExemptions.type]: (state, {payload}) => {
            const {planPartId, chargeRuleExemptions} = payload;

            return updatePlanPart(state, planPartId, planPart =>
                planPart.merge({
                    chargeRuleExemptions,
                }),
            );
        },
        [PlanPricingActions.resetPlanPartTemplate.type]: (state: any) => {
            const updatedPlanParts = state.planParts.map(part => {
                const planPartWithoutBookings = PlanHelpers.removeAllBookingsFromPlanPart(part);
                const planPartWithoutDiscounts = PlanHelpers.removeAllDiscountsFromPlanPart(planPartWithoutBookings);

                return planPartWithoutDiscounts.merge({
                    billing: new Billing(),
                    billingProfileId: null,
                });
            });

            return state.merge({
                planParts: updatedPlanParts,
                publicFundingSettings: null,
            });
        },
        [Actions.removeAllBookings.type]: (state, {payload}) => {
            return updatePlanPart(state, payload.planPartId, planPart =>
                PlanHelpers.removeAllBookingsFromPlanPart(planPart),
            );
        },
        [Actions.packageDidChange.type]: (state, {payload}) => {
            const {planPartId, planPackage, session, oldPackage} = payload;

            return updatePlanPart(state, planPartId, part =>
                PlanHelpers.updatePlanPartPackage(part, planPackage, session, oldPackage),
            );
        },
        [Actions.packagesDidChange.type]: (state, {payload}) => {
            const {planPartId, packages} = payload;

            return updatePlanPart(state, planPartId, part => PlanHelpers.setPlanPartPackages(part, packages));
        },
        [PlanPricingActions.addDiscount.type]: (state, {payload}) => {
            const {isSurcharge, planPartId, addId, preset} = payload;

            return updatePlanPart(state, planPartId, part => {
                const isPreset = Boolean(preset);
                const isByAmount = addId === CostHelpers.DISCOUNT_ORIGIN_CUSTOM_BY_AMOUNT;
                const customTitle = isSurcharge ? i18next.t('discounts.surcharge') : i18next.t('discounts.discount');
                const isActualsV2 = CostHelpers.billingIsActualsV2(part.billing);
                const discounts = part.discounts.push(
                    new Plan.records!.Discount({
                        isSurcharge,
                        ordering: isPreset ? preset.ordering : null,
                        title: isPreset ? preset.title : customTitle,
                        origin: isPreset ? CostHelpers.DISCOUNT_ORIGIN_PRESET : CostHelpers.DISCOUNT_ORIGIN_CUSTOM,
                        isPercent: !isByAmount,
                        amount: isPreset ? preset.amount / 100 : 0,
                        // Actuals V2 plans should use weekly discounts
                        period: isByAmount && !isActualsV2 ? 'MONTHLY' : 'WEEKLY',
                        discountPresetId: isPreset ? preset.discountPresetId : null,
                        discountId: generateUUID(),
                    }),
                );
                return part.merge({
                    discounts: PlanHelpers.sortDiscounts(discounts),
                });
            });
        },
        [PlanPricingActions.discountDidChange.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount => {
                const parsedValue = parseDecimalToFloat(payload.newDiscount);
                const newAmount = discount.isPercent ? (parsedValue ?? 0) / 100 : parsedValue ?? 0;

                return discount.merge({
                    amount: discount.isSurcharge ? -newAmount : newAmount,
                });
            });
        },
        [PlanPricingActions.discountFrequencyChanged.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount =>
                discount.merge({
                    period: payload.newDiscountFrequency,
                }),
            );
        },
        [PlanPricingActions.discountTitleDidChange.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount =>
                discount.merge({
                    title: payload.newTitle,
                }),
            );
        },
        [PlanPricingActions.convertDiscountOrigin.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount =>
                discount.merge({
                    origin: payload.discountOrigin,
                }),
            );
        },
        [PlanPricingActions.convertToPercentage.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount =>
                discount.merge({
                    isPercent: true,
                    period: 'WEEKLY',
                    amount: payload.newDiscount,
                }),
            );
        },
        [PlanPricingActions.discountShowOnInvoice.type]: (state, {payload}) => {
            return updateDiscountOnPlanPart(state, payload.planPartId, payload.discountId, discount =>
                discount.merge({
                    showOnInvoice: payload.show,
                }),
            );
        },
        [PlanPricingActions.removeDiscount.type]: (state, {payload}) => {
            return updatePlanPart(state, payload.planPartId, part =>
                part.merge({
                    discounts: part.discounts.filter(discount => discount.discountId !== payload.discountId),
                }),
            );
        },
        [Actions.setBillingProfile.type]: (state, {payload}) => {
            const {billingProfile} = payload;

            const isFirst = state.planParts.findIndex(part => part.planPartId === payload.planPartId) === 0;

            if (!billingProfile || !isFirst) {
                return updatePlanPart(state, payload.planPartId, part => {
                    const {billingProfile} = payload;
                    return PlanHelpers.setBillingProfileOnPlanPart(part, billingProfile, payload.override);
                });
            }

            const newBilling = PlanHelpers.getBillingFromBillingProfile(billingProfile);

            const isActuals = CostHelpers.billingIsActuals(newBilling);
            const isMonthly = CostHelpers.billingIsMonthly(newBilling);

            let tempState = state;
            if (state.publicFundingSettings && (isActuals || isMonthly)) {
                const period = isMonthly ? 'MONTHLY' : 'WEEKLY';

                const publicFundingSettings =
                    state.publicFundingSettings.method === 'MANUAL' || state.publicFundingSettings.method === 'ASSISTED'
                        ? state.publicFundingSettings.merge({
                              period,
                          })
                        : state.publicFundingSettings;
                tempState = tempState.merge({
                    publicFundingSettings,
                });
            }

            return updatePlanPart(tempState, payload.planPartId, part => {
                const {billingProfile} = payload;
                return PlanHelpers.setBillingProfileOnPlanPart(part, billingProfile, payload.override);
            });
        },
        [PlanPricingActions.updateDiscountPreset.type]: (state, {payload}) => {
            return updatePlanPart(state, payload.planPartId, part => {
                const discountIndex = part.discounts.findIndex(
                    discount => discount.discountPresetId === payload.preset.discountPresetId,
                );
                if (discountIndex < 0) {
                    return part;
                }

                return part.merge({
                    discounts: part.discounts.update(discountIndex, discount =>
                        discount.merge({
                            amount: payload.preset.amount / 100,
                        }),
                    ),
                });
            });
        },
        [Actions.previewPlanSuccess.type]: (state, {payload}) => {
            const planModel = payload.plan;

            if (payload.isAutomaticChargesEnabled) {
                return state.merge({
                    behaviors: planModel.behaviors,
                    from: planModel.from,
                    to: planModel.to,
                    planStates: planModel.planStates,
                    publicFundingSettings: planModel.publicFundingSettings,
                    planParts: planModel.planParts,
                });
            }

            // We don't want to override the entire edit plan.
            // e.g. this can update ids and the cursor will be lost in discounts
            const updatedPlan = state.merge({
                behaviors: planModel.behaviors,
                from: planModel.from,
                to: planModel.to,
                planStates: planModel.planStates,
                publicFundingSettings: planModel.publicFundingSettings,
            });

            // Update the plan parts with new session bookings to be able to infer plan part estimates
            // and funded bookings from public funding
            return state.planParts.reduce((reduction, current) => {
                const newPartIndex = planModel.planParts.findIndex(part => part.planPartId === current.planPartId);
                if (newPartIndex < 0) {
                    return reduction;
                }
                const newPart = planModel.planParts.get(newPartIndex);
                const oldPartIndex = reduction.planParts.findIndex(part => part.planPartId === newPart.planPartId);
                if (oldPartIndex < 0) {
                    return reduction;
                }

                return reduction.merge({
                    planParts: reduction.planParts.update(oldPartIndex, part =>
                        part.merge({
                            sessionBookings: newPart.sessionBookings,
                            productBookings: newPart.productBookings,
                        }),
                    ),
                });
            }, updatedPlan);
        },

        [PublicFundingModalActions.save.type]: (state, {payload}) => {
            return state.merge({
                publicFundingSettings: payload.publicFundingSettings,
            });
        },

        [PlanPricingActions.removePublicFunding.type]: state => {
            return state.merge({
                publicFundingSettings: null,
            });
        },

        [Actions.createPlanSuccess.type]: (_, {payload}) => payload.plan,
        [Actions.updatePlanSuccess.type]: (_, {payload}) => payload.plan,
        [Actions.closeManagePlan.type]: () => {
            return Plan.default;
        },
    },
    Plan.default,
);

export default reducer;
